import { ButtonHTMLAttributes, Children, forwardRef, useCallback, useState } from "react";
import classNames from "classnames";
import { RefType } from "../../types";
import { genericMemo } from "../../utils";
import { Button, ButtonProps } from "../Button";
import { LoadingIndicator, LoadingIndicatorProps } from "../LoadingIndicator";
import { mapButtonSizeToLoadingIndicatorSize } from "./utils";

import styles from "./styles.module.scss";

type ExpectedProps = {
  onClick?: (...arg: any[]) => unknown;
};

export type ProgressButtonProps<
  P extends ExpectedProps = ButtonHTMLAttributes<HTMLButtonElement>,
  R extends unknown = HTMLButtonElement
> = ButtonProps<P, R> & LoadingIndicatorProps & {
  processing?: boolean;
};

const ProgressButtonComponent = <
  P extends ExpectedProps = ButtonHTMLAttributes<HTMLButtonElement>,
  R extends unknown = HTMLButtonElement
>(
  props: ProgressButtonProps<P, R>,
) => <></>;

type ProgressButtonComponentType = typeof ProgressButtonComponent;

export const ProgressButton = genericMemo(
  forwardRef(
    <
      P extends ExpectedProps = ButtonHTMLAttributes<HTMLButtonElement>,
      R extends unknown = HTMLButtonElement
    >(
      {
        onClick,
        disabled,
        processing: externalProcessing,
        loadingIndicatorSize,
        children,
        className,
        ...props
      }: ProgressButtonProps<P, R>,
      ref: RefType<R>,
    ) => {
      const [ processing, setProcessing ] = useState(false);
      const isProcessing = processing || externalProcessing;

      return (
        <Button<any, R>
          {...props}
          ref={ref}
          className={classNames(styles.button, className, {
            [styles.processing]: isProcessing,
          })}
          disabled={isProcessing || disabled}
          onClick={useCallback(
            async (...args: any[]) => {
              try {
                setProcessing(true);
                await onClick?.(...args);
              } finally {
                setProcessing(false);
              }
            },
            [ setProcessing, onClick ],
          )}
        >
          {isProcessing && (
            <LoadingIndicator
              loadingIndicatorSize={loadingIndicatorSize ?? mapButtonSizeToLoadingIndicatorSize(props.buttonSize)}
              loadingIndicatorType={props.loadingIndicatorType}
              loadingIndicatorLabel=""
              className={styles.loader}
            />
          )}
          {Children.map(children, child => typeof child === "string" ? <span>{child}</span> : child)}
        </Button>
      );
    },
  ) as ProgressButtonComponentType,
);
